import { useQuery } from "@apollo/client";
import { compact, sortBy, uniqBy } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import {
  GetAssessmentAnswerCalibrationsQuery,
  GetAssessmentAnswerCalibrationsQueryVariables,
} from "types/graphql-schema";

import getAssessmentAnswerCalibrationsQuery from "@apps/reporting/graphql/get-assessment-answer-calibrations-query";
import { assertEdgesNonNull } from "@helpers/helpers";

const useAssessmentCalibrationData = ({
  complianceProgramId,
  templateId,
  targetId,
}: {
  complianceProgramId?: number | null;
  templateId?: number | null;
  targetId?: number | null;
}) => {
  const { data, loading } = useQuery<
    GetAssessmentAnswerCalibrationsQuery,
    GetAssessmentAnswerCalibrationsQueryVariables
  >(getAssessmentAnswerCalibrationsQuery, {
    variables: {
      complianceProgramId: complianceProgramId ?? 0,
      templateId: templateId ?? 0,
      targetId: targetId ?? 0,
    },
    skip: !complianceProgramId || !templateId || !targetId,
    fetchPolicy: "cache-and-network",
  });

  const { allCalibrations, questions } = useMemo(() => {
    const flattened = data?.assessmentCalibrations
      ? assertEdgesNonNull(data.assessmentCalibrations)
      : [];

    const allCalibrations = sortBy(
      flattened,
      (calibration) => -moment(calibration.created).unix()
    );

    const questions = uniqBy(
      compact(allCalibrations.map((calibration) => calibration.question)),
      (question) => question.id
    );

    return {
      allCalibrations,
      questions,
    };
  }, [data]);

  return {
    allCalibrations,
    questions,
    loading,
  };
};

export default useAssessmentCalibrationData;
